import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Navbar } from "@/components/Navbar";
import { AccountTypeSwitch } from "@/components/AccountTypeSwitch";
import { useLang } from "@/lib/i18n";
import api, { apiError } from "@/lib/api";

const EMPTY = { name: "", slug: "", city: "", country: "", description: "", contact_name: "", contact_email: "", contact_password: "", message: "" };

export default function RegisterAssociation() {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "slug" ? e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/association-requests", form);
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background blueprint-grid">
      <Navbar />
      <div className="max-w-xl mx-auto px-4 pt-12 sm:pt-16 pb-16">
        <div className="border border-border rounded-xl bg-card p-8 fade-up">
          {sent ? (
            <div className="text-center py-8" data-testid="assoc-request-sent">
              <CheckCircle2 className="h-12 w-12 text-accent mx-auto mb-5" />
              <h1 className="font-heading font-extrabold text-2xl tracking-tight">{t("assoc_request_sent_title")}</h1>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{t("assoc_request_sent_text")}</p>
              <Link to="/" data-testid="back-home-link" className="text-sm text-accent hover:underline block mt-6">{t("back")}</Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading font-extrabold text-2xl tracking-tight">{t("register_association_title")}</h1>
              <div className="mt-6">
                <AccountTypeSwitch active="assoc" />
              </div>
              <p className="text-sm text-muted-foreground mt-2">{t("register_association_sub")}</p>
              <form onSubmit={submit} className="mt-6 space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>{t("assoc_name")}</Label>
                    <Input data-testid="areq-name" value={form.name} onChange={set("name")} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("slug")}</Label>
                    <Input data-testid="areq-slug" value={form.slug} onChange={set("slug")} placeholder="lyon" required />
                    <p className="text-[11px] font-mono-data text-muted-foreground">shialoc.app/{form.slug || "..."}</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>{t("city")}</Label><Input data-testid="areq-city" value={form.city} onChange={set("city")} /></div>
                  <div className="space-y-1.5"><Label>{t("country")}</Label><Input data-testid="areq-country" value={form.country} onChange={set("country")} /></div>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("description")}</Label>
                  <Textarea rows={2} data-testid="areq-description" value={form.description} onChange={set("description")} />
                </div>
                <div className="border-t border-border pt-5 space-y-4">
                  <p className="text-xs font-mono-data uppercase tracking-wider text-muted-foreground">{t("your_credentials")}</p>
                  <div className="space-y-1.5"><Label>{t("name")}</Label><Input data-testid="areq-contact-name" value={form.contact_name} onChange={set("contact_name")} required /></div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label>{t("email")}</Label><Input type="email" data-testid="areq-contact-email" value={form.contact_email} onChange={set("contact_email")} required /></div>
                    <div className="space-y-1.5"><Label>{t("password")}</Label><Input type="password" data-testid="areq-contact-password" value={form.contact_password} onChange={set("contact_password")} required minLength={6} /></div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("message_optional")}</Label>
                  <Textarea rows={2} data-testid="areq-message" value={form.message} onChange={set("message")} />
                </div>
                {error && <p data-testid="areq-error" className="text-sm text-destructive">{error}</p>}
                <Button type="submit" data-testid="areq-submit" disabled={loading || !form.name || !form.slug || !form.contact_name || !form.contact_email || form.contact_password.length < 6} className="w-full rounded-full active:scale-95">
                  {loading ? "..." : t("send_request")}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
